import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import DateInput from '../../components/common/DateInput';

function Field({ label, children, span = 1 }) {
  const spanClass = span === 4 ? 'col-span-4' : span === 2 ? 'col-span-2' : '';
  return (
    <div className={spanClass}>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

function SectionCard({ number, title, children, cols = 3 }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
        <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">{number}</span>
        {title}
      </h2>
      <div className={`grid grid-cols-${cols === 4 ? 4 : 3} gap-4`}>
        {children}
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';

const STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Andaman and Nicobar Islands','Chandigarh','Dadra & Nagar Haveli and Daman & Diu','Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'];

function EditStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { students, classes, feeStructures, routes, updateStudent } = useApp();

  const [form, setForm] = useState(null);
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  useEffect(() => {
    const student = students.find(s => s.id === id);
    if (student) {
      setForm({
        guardianType: student.fatherName ? 'father' : student.motherName ? 'mother' : 'other',
        guardianName: '', guardianRelation: '', guardianPhone: '', guardianOccupation: '',
        feeGroup: '', feeStructureId: '',
        ...student,
      });
    }
  }, [id, students]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok = await updateStudent(id, form);
    if (ok) navigate('/students');
  };

  if (!form) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        Student not found.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => navigate('/students')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft size={18} className="text-gray-500" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Edit Student</h1>
          <p className="text-gray-500 text-sm mt-0.5">{form.firstName} {form.lastName} — {form.admissionNumber}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* 1. Personal Information */}
        <SectionCard number="1" title="Personal Information" cols={4}>
          <Field label="First Name">
            <input required value={form.firstName||''} onChange={e => set('firstName', e.target.value)} className={inputCls} placeholder="e.g. Arjun" />
          </Field>
          <Field label="Last Name">
            <input required value={form.lastName||''} onChange={e => set('lastName', e.target.value)} className={inputCls} placeholder="e.g. Kumar" />
          </Field>
          <Field label="Date of Birth">
            <DateInput value={form.dob||''} onChange={e => set('dob', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Gender">
            <select value={form.gender||'boy'} onChange={e => set('gender', e.target.value)} className={inputCls}>
              {['boy','girl'].map(o => <option key={o} value={o}>{o.charAt(0).toUpperCase()+o.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Blood Group">
            <select value={form.bloodGroup||''} onChange={e => set('bloodGroup', e.target.value)} className={inputCls}>
              {['A+','A-','B+','B-','O+','O-','AB+','AB-'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Phone">
            <input type="tel" value={form.phone||''} onChange={e => set('phone', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Email" span={2}>
            <input type="email" value={form.email||''} onChange={e => set('email', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Address (House / Street)" span={4}>
            <input value={form.address||''} onChange={e => set('address', e.target.value)} className={inputCls} placeholder="House no., street, locality" />
          </Field>
          <Field label="City / Town">
            <input value={form.city||''} onChange={e => set('city', e.target.value)} className={inputCls} />
          </Field>
          <Field label="District">
            <input value={form.district||''} onChange={e => set('district', e.target.value)} className={inputCls} />
          </Field>
          <Field label="PIN Code">
            <input value={form.pinCode||''} onChange={e => set('pinCode', e.target.value)} className={inputCls} maxLength={6} />
          </Field>
          <Field label="State">
            <select value={form.state||''} onChange={e => set('state', e.target.value)} className={inputCls}>
              <option value="">Select State</option>
              {STATES.map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>

          <div className="col-span-4 border-t border-gray-100 pt-2 mt-1">
            <p className="font-semibold">Father's Details</p>
          </div>
          <Field label="Father's Name">
            <input value={form.fatherName||''} onChange={e => set('fatherName', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Father's Occupation">
            <input value={form.fatherOccupation||''} onChange={e => set('fatherOccupation', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Father's Phone">
            <input type="tel" value={form.fatherPhone||''} onChange={e => set('fatherPhone', e.target.value)} className={inputCls} />
          </Field>

          <div className="col-span-4 border-t border-gray-100 pt-2 mt-1">
            <p className="font-semibold">Mother's Details</p>
          </div>
          <Field label="Mother's Name">
            <input value={form.motherName||''} onChange={e => set('motherName', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Mother's Occupation">
            <input value={form.motherOccupation||''} onChange={e => set('motherOccupation', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Mother's Phone">
            <input type="tel" value={form.motherPhone||''} onChange={e => set('motherPhone', e.target.value)} className={inputCls} />
          </Field>
        </SectionCard>

        {/* 2. Identity & Community */}
        <SectionCard number="2" title="Identity & Community">
          <Field label="Category">
            <select value={form.category||'General'} onChange={e => set('category', e.target.value)} className={inputCls}>
              {['General','OBC','SC','ST','EWS','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Caste">
            <input value={form.caste||''} onChange={e => set('caste', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Religion">
            <select value={form.religion||''} onChange={e => set('religion', e.target.value)} className={inputCls}>
              <option value="">Select Religion</option>
              {['Hindu','Muslim','Christian','Sikh','Buddhist','Jain','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Aadhar Number">
            <input value={form.aadharNumber||''} onChange={e => set('aadharNumber', e.target.value)} className={inputCls} maxLength={14} />
          </Field>
          <Field label="PEN Number">
            <input value={form.penNumber||''} onChange={e => set('penNumber', e.target.value)} className={inputCls} />
          </Field>
        </SectionCard>

        {/* 3. Academic Details */}
        <SectionCard number="3" title="Academic Details" cols={4}>
          <Field label="Class">
            <select required value={form.classId||''} onChange={e => {
              const cid = e.target.value;
              const fs = feeStructures.find(f => f.classId === cid);
              setForm(f => ({ ...f, classId: cid, feeStructureId: fs?.id || '' }));
            }} className={inputCls}>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name} — Section {c.section}</option>)}
            </select>
          </Field>
          <Field label="Section">
            <select value={form.sectionId||'A'} onChange={e => set('sectionId', e.target.value)} className={inputCls}>
              {['A','B','C','D'].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Roll Number">
            <input value={form.rollNumber||''} onChange={e => set('rollNumber', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Admission Number">
            <input value={form.admissionNumber||''} onChange={e => set('admissionNumber', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Date of Admission">
            <DateInput value={form.admissionDate||''} onChange={e => set('admissionDate', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Fee Group">
            <select value={form.feeGroup||''} onChange={e => set('feeGroup', e.target.value)} className={inputCls}>
              <option value="">Select Fee Group</option>
              {['General Fee','RTE','Scholarship','Staff Ward','Other'].map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Status" span={2}>
            <select value={form.status||'active'} onChange={e => set('status', e.target.value)} className={inputCls}>
              {['active','inactive','transferred','graduated'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </Field>
        </SectionCard>

        {/* 4. Transport */}
        <SectionCard number="4" title="Transport (Optional)">
          {(() => {
            const selectedRoute = routes.find(r => r.id === form.transportRouteId);
            const stops = selectedRoute?.stops || [];
            const selectedStop = stops.find(s => s.name === form.transportStop);
            return (<>
              <Field label="Route">
                <select value={form.transportRouteId||''} onChange={e => set('transportRouteId', e.target.value)} className={inputCls}>
                  <option value="">— No Transport —</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.name || r.routeName} ({r.startPoint} → {r.endPoint})</option>)}
                </select>
              </Field>
              <Field label="Stop">
                <select value={form.transportStop||''} onChange={e => set('transportStop', e.target.value)} className={inputCls} disabled={!form.transportRouteId}>
                  <option value="">— Select Stop —</option>
                  {stops.map(s => <option key={s.name} value={s.name}>{s.name}{s.fee ? ` — ₹${Number(s.fee).toLocaleString('en-IN')}` : ''}</option>)}
                </select>
              </Field>
              <Field label="Annual Transport Fee">
                <div className="px-3 py-1 border border-gray-200 rounded-lg text-sm bg-gray-50 font-semibold text-indigo-700">
                  {selectedStop?.fee ? `₹${Number(selectedStop.fee).toLocaleString('en-IN')}` : '—'}
                </div>
              </Field>
            </>);
          })()}
        </SectionCard>

        {/* Actions */}
        <div className="flex justify-end gap-3 pb-6">
          <button type="button" onClick={() => navigate('/students')}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit"
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
            <Save size={16} /> Save Changes
          </button>
        </div>

      </form>
    </div>
  );
}

export default EditStudent;
